"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.refundableReceiptsResponseSchema = exports.refundableReceiptSchema = exports.refundableInvoiceLineSchema = exports.PROCESS_REFUND_RESPONSE_EXAMPLE = void 0;
const zod_1 = require("zod");
const financial_common_response_1 = require("./financial-common.response");
exports.PROCESS_REFUND_RESPONSE_EXAMPLE = {
    refundPayment: {
        ...financial_common_response_1.FIN_PAYMENT_RESPONSE_EXAMPLE,
        id: 'ffffffff-0000-0000-0000-000000000000',
        paymentNumber: 'PAY000002',
        transactionType: 'REFUND',
        totalAmount: 50000,
    },
    updatedReceipt: { ...financial_common_response_1.FIN_RECEIPT_RESPONSE_EXAMPLE, refundPaymentId: 'ffffffff-0000-0000-0000-000000000000' },
    notificationSent: false,
};
/* ------------------------------------------------------------------------- *
 * REFUNDABLE RECEIPTS — what `GET /financial/refunds/receipts` returns.
 *
 * A refund is taken against a RECEIPT, not against a bill (`processRefund`
 * resolves the receipt, then its payment, then the mode to refund on). Nothing
 * in this system could list a patient's receipts, so every refund screen asked
 * the operator to paste a receipt UUID — which is why the Appointment desk had
 * no way to refund a consultation fee it had just collected.
 *
 * `refundablePaise` IS THE HEADROOM, not the receipt total: partial refunds are
 * allowed and accumulate on `fin_receipt_refunds`, so a receipt already refunded
 * in part must not offer its whole value again. The server computes it because
 * the server is what will refuse it (`validateRefundDoesNotExceedOriginal`), and
 * two answers to "how much is left" is how an operator gets a rejection they
 * cannot explain.
 * ------------------------------------------------------------------------- */
/**
 * ONE BILLED LINE on a receipt, and what is still refundable against it.
 *
 * Added so a refund can be ATTRIBUTED. Without it the screen knew a receipt
 * settled invoice "INV2026000140" and nothing more, so a partial refund could
 * not say which of its lines it reversed — and the laboratory, correctly,
 * refused to guess.
 *
 * `refundablePaise` is the SERVER's arithmetic, exactly like the receipt-level
 * figure below: `validateRefundDoesNotExceedOriginal` is what accepts or
 * rejects, and a second opinion computed in the browser is how an operator gets
 * a rejection they cannot explain.
 */
exports.refundableInvoiceLineSchema = zod_1.z.object({
    invoiceLineId: zod_1.z.string().uuid(),
    invoiceId: zod_1.z.string().uuid(),
    invoiceNumber: zod_1.z.string(),
    /** What the bill called it — the snapshot, not a live master lookup. */
    description: zod_1.z.string(),
    chargeCode: zod_1.z.string().nullable(),
    quantity: zod_1.z.number().nullable(),
    unitRatePaise: zod_1.z.number().int().nullable(),
    netAmountPaise: zod_1.z.number().int(),
    refundedPaise: zod_1.z.number().int(),
    refundablePaise: zod_1.z.number().int(),
    /**
     * Resolved through `FinInvoiceLine.chargeId -> ChargeMaster <- LabTest.chargeId`
     * — the same FK the billing bridge walks. NEVER by charge-code prefix, which
     * is partly an alphabetical bucket, and never by name.
     */
    isLaboratory: zod_1.z.boolean(),
    /** The laboratory test code, when this line is one. Display only. */
    testCode: zod_1.z.string().nullable(),
});
exports.refundableReceiptSchema = zod_1.z.object({
    receiptId: zod_1.z.string().uuid(),
    receiptNumber: zod_1.z.string(),
    /** The PAYMENT's date — the day the money was taken, which is what an operator recognises. */
    receiptDate: zod_1.z.string(),
    amountPaise: zod_1.z.number().int(),
    refundedPaise: zod_1.z.number().int(),
    refundablePaise: zod_1.z.number().int(),
    /** Display only. The refund is issued on the ORIGINAL mode regardless of what is shown. */
    paymentModeName: zod_1.z.string().nullable(),
    /** The bills this receipt settled, so the operator can tell two same-value receipts apart. */
    invoiceNumbers: zod_1.z.array(zod_1.z.string()),
    /**
     * The individual billed lines, so a refund can name the one it reverses.
     * Empty when the receipt settled no invoice line the operator could pick.
     */
    lines: zod_1.z.array(exports.refundableInvoiceLineSchema),
});
exports.refundableReceiptsResponseSchema = zod_1.z.object({
    receipts: zod_1.z.array(exports.refundableReceiptSchema),
});
//# sourceMappingURL=financial-refund.response.js.map